import { useContext, useEffect, useRef, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Button,
  useTheme
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import ScoutMark from "../../atoms/icons/ScoutMark";
import Link from "../../atoms/mui/Link";
import {
  UserMessage,
  AssistantMessage,
  ThinkingIndicator
} from "../scout/ScoutMessages";

import { fetchOurApi, shareProductBom } from "../../../helpers/utils/apiCalls";
import {
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";
import { analyticsTrack } from "../../../helpers/analytics";
import useExplorerLayoutContext from "../../../helpers/contexts/explorerLayout";

import { SurveyContext } from "../survey-shape/SurveyContext";

import { applyBomFieldUpdate } from "./bomFieldHandlers";
import { isBomSurveyComplete } from "./helpers";

const RAIL_WIDTH = 48;

const buildBomOpener = ({ buyerName, productName }) => {
  const safeBuyer = buyerName || "Your customer";
  const safeProduct = productName || "this product";
  return `Hi — ${safeBuyer} is asking you to confirm the BOM for "${safeProduct}." Let's walk through it together. First, what's the total weight of the product?`;
};

const PanelHeader = ({ isOpen, onToggle }) => {
  const { palette } = useTheme();
  return (
    <Box
      p={1.5}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      style={{
        flexShrink: 0,
        borderBottom: `1px solid ${palette.backgroundGray.dark}`
      }}
    >
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <ScoutMark size={28} />
        {isOpen && (
          <Typography variant="subtitle1" color="textPrimary">
            Scout
          </Typography>
        )}
      </Box>
      <Tooltip title={isOpen ? "Collapse Scout" : "Expand Scout"}>
        <IconButton size="small" onClick={onToggle}>
          {isOpen ? (
            <ChevronLeftIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ShareActionCard = ({ buyerName, onShare, disabled }) => {
  const { palette } = useTheme();
  return (
    <Box
      p={2}
      style={{
        backgroundColor: palette.backgroundGray.light,
        border: `1px solid ${palette.primary.main}`,
        borderRadius: "8px"
      }}
    >
      <Typography variant="subtitle2" color="primary">
        Ready to share?
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {buyerName || "Your customer"} will see this product&apos;s materials,
        weight, packaging, and shipping route. You can revoke any time.
      </Typography>
      <Box mt={1}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onShare}
          disabled={disabled}
        >
          Share with {buyerName || "customer"}
        </Button>
      </Box>
    </Box>
  );
};

const BomSurveyScoutPanel = () => {
  const { palette } = useTheme();
  const context = useContext(SurveyContext);
  const {
    vendorId,
    currentBomValues,
    buyerName,
    productName,
    productId,
    lifecycleEventId,
    scoutConversationId,
    setScoutConversationId,
    isScoutCollapsed,
    setIsScoutCollapsed,
    setBomField,
    backToDashboardHref
  } = context || {};

  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const [conversations, conversationsLoading] = useCachedDisplayData(
    "agent-conversations"
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareDone, setShareDone] = useState(false);
  const { activateSnackbar } = useExplorerLayoutContext() || {};
  const hydratedRef = useRef(false);
  const desktopTranscriptRef = useRef(null);
  const mobileTranscriptRef = useRef(null);
  const desktopReplyInputRef = useRef(null);
  const mobileReplyInputRef = useRef(null);

  useEffect(() => {
    if (hydratedRef.current) {
      return;
    }
    if (conversationsLoading) {
      return;
    }
    const persistedConv = scoutConversationId
      ? (conversations || []).find((c) => c.id === scoutConversationId)
      : null;
    const persistedTurns = persistedConv?.turns || [];
    hydratedRef.current = true;
    if (persistedTurns.length > 0) {
      setMessages(
        persistedTurns.map(({ role, content }) => ({ role, content }))
      );
      return;
    }
    setMessages([
      {
        role: "assistant",
        content: buildBomOpener({ buyerName, productName })
      }
    ]);
  }, [
    scoutConversationId,
    conversations,
    conversationsLoading,
    buyerName,
    productName
  ]);

  useEffect(() => {
    const scrollToBottom = (node) => {
      if (!node) {
        return;
      }
      node.scrollTop = node.scrollHeight;
    };
    requestAnimationFrame(() => {
      scrollToBottom(desktopTranscriptRef.current);
      scrollToBottom(mobileTranscriptRef.current);
    });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isLoading || messages.length === 0) {
      return;
    }
    desktopReplyInputRef.current?.focus();
    mobileReplyInputRef.current?.focus();
  }, [isLoading, messages.length]);

  const sendMessage = async (messageOverride) => {
    const trimmed = (messageOverride || input).trim();
    if (!trimmed || isLoading) {
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsLoading(true);

    analyticsTrack("bom_survey_message_sent", {
      companyId: vendorId,
      lifecycleEventId
    });

    try {
      const conversationId = scoutConversationId || `bom-${lifecycleEventId}`;
      if (!scoutConversationId && setScoutConversationId) {
        setScoutConversationId(conversationId);
      }
      await fetchOurApi({
        path: "/agents/chat-bom-survey",
        method: "POST",
        data: {
          companyId: vendorId,
          conversationId,
          message: trimmed,
          lifecycleEventId,
          productId,
          productName,
          buyerName,
          currentValues: currentBomValues || {}
        },
        callback: (result) => {
          const safeResult = result || {};
          const fieldUpdates = safeResult.fieldUpdates || [];
          const fieldErrors = safeResult.fieldErrors || [];
          fieldUpdates.forEach((fieldUpdate) => {
            const applied = applyBomFieldUpdate({
              fieldUpdate,
              context: { setBomField }
            });
            if (applied) {
              analyticsTrack("bom_field_auto_populated", {
                companyId: vendorId,
                lifecycleEventId,
                field: fieldUpdate.field
              });
            }
          });
          fieldErrors.forEach((fieldError) => {
            console.warn("[bom-survey] field write failed:", fieldError);
            activateSnackbar?.({
              severity: "error",
              message: `Scout tried to save ${fieldError.field} but the write failed. Try entering it directly on the right.`
            });
          });
          const content =
            safeResult.response ||
            (fieldUpdates.length
              ? "Got it."
              : "Sorry, I didn't quite catch that. Could you rephrase?");
          setMessages((prev) => [...prev, { role: "assistant", content }]);
        }
      });
    } catch (err) {
      console.error("[bom-survey] chat threw", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I hit a snag connecting. Could you try again?"
        }
      ]);
    }

    setIsLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const bomComplete = isBomSurveyComplete(currentBomValues || {});

  const handleShare = async () => {
    if (
      !bomComplete ||
      isSharing ||
      shareDone ||
      !lifecycleEventId ||
      !vendorId
    ) {
      return;
    }
    setIsSharing(true);
    try {
      const response = await shareProductBom({
        lifecycleEventId,
        vendorId,
        share: true
      });
      if (response?.error) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "That didn't go through — try again?"
          }
        ]);
        activateSnackbar?.({
          severity: "error",
          message: `Couldn't share the BOM with ${buyerName || "your customer"}. Try again?`
        });
        setIsSharing(false);
        return;
      }
      await updateCollectionDoc(
        "vendor-assigned-product-lifecycle-events",
        lifecycleEventId,
        { bomStatus: "confirmed" }
      );
      analyticsTrack("bom_survey_shared", {
        companyId: vendorId,
        lifecycleEventId
      });
      activateSnackbar?.({
        severity: "success",
        message: `Shared the BOM for "${productName || "this product"}" with ${buyerName || "your customer"}.`
      });
      setShareDone(true);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Done — your BOM is shared with ${buyerName || "your customer"}.`
        }
      ]);
    } catch (err) {
      console.error("[bom-survey] share threw", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "That didn't go through — try again?"
        }
      ]);
      activateSnackbar?.({
        severity: "error",
        message: `Couldn't share the BOM with ${buyerName || "your customer"}. Check your connection and try again.`
      });
    }
    setIsSharing(false);
  };

  const handleToggleCollapse = () => {
    if (setIsScoutCollapsed) {
      setIsScoutCollapsed(!isScoutCollapsed);
    }
  };

  const renderTranscript = (transcriptRef) => (
    <Box
      ref={transcriptRef}
      px={2}
      py={1}
      flexGrow={1}
      display="flex"
      flexDirection="column"
      style={{ gap: 12, overflowY: "auto", minHeight: 0 }}
    >
      {messages.map((msg, idx) =>
        msg.role === "user" ? (
          <UserMessage key={`bom-msg-${idx}`} content={msg.content} />
        ) : (
          <AssistantMessage key={`bom-msg-${idx}`} content={msg.content} />
        )
      )}
      {bomComplete && !shareDone && (
        <ShareActionCard
          buyerName={buyerName}
          onShare={handleShare}
          disabled={isSharing}
        />
      )}
      {shareDone && (
        <Box mt={1}>
          <Link
            href={backToDashboardHref || "/explorer/dashboard?view=products"}
          >
            <Button variant="outlined" size="small">
              Return to Dashboard
            </Button>
          </Link>
        </Box>
      )}
      {isLoading && <ThinkingIndicator />}
    </Box>
  );

  const renderFooter = (inputRef) => (
    <Box p={2} style={{ flexShrink: 0 }}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Answer or ask Scout anything"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        multiline
        maxRows={4}
        disabled={isLoading}
        inputRef={inputRef}
      />
    </Box>
  );

  return (
    <>
      {isScoutCollapsed ? (
        <Box
          sx={{ display: { xs: "none", md: "flex" } }}
          flexDirection="column"
          style={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            background: palette.common.white,
            borderRight: `1px solid ${palette.backgroundGray.dark}`,
            transition: "width 200ms ease",
            height: "100%"
          }}
        >
          <PanelHeader isOpen={false} onToggle={handleToggleCollapse} />
        </Box>
      ) : (
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: "1 1 60%",
            maxWidth: "60%",
            minWidth: 360
          }}
          flexDirection="column"
          style={{
            background: palette.common.white,
            borderRight: `1px solid ${palette.backgroundGray.dark}`,
            transition: "width 200ms ease",
            height: "100%"
          }}
        >
          <PanelHeader isOpen onToggle={handleToggleCollapse} />
          {renderTranscript(desktopTranscriptRef)}
          {renderFooter(desktopReplyInputRef)}
        </Box>
      )}

      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          width: "100%",
          flexGrow: 1
        }}
        flexDirection="column"
        style={{
          background: palette.common.white,
          height: "100%",
          minHeight: 0
        }}
      >
        <PanelHeader isOpen onToggle={handleToggleCollapse} />
        {renderTranscript(mobileTranscriptRef)}
        {renderFooter(mobileReplyInputRef)}
      </Box>
    </>
  );
};

export default BomSurveyScoutPanel;
